import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../../utils/axios';
import { toastError, toastSuccess } from '../../utils/toast';
import { uploadPhotoToCloudflare, runWithConcurrencyLimit } from '../../utils/photoUpload';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import useConfirmModal from '../ConfirmModal/useConfirmModal';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import Tray from './Tray';
import TargetsList from './TargetsList';
import './BulkPhotosModal.css';

function createPhotoId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `tmp-${crypto.randomUUID()}`;
    }
    return `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function BulkPhotosModal({
    open,
    bulkDrafts = [],
    onClose,
    onSaved,
}) {
    const confirmModal = useConfirmModal();
    const uploadSessionRef = useRef(0);
    const modalRef = useRef(null);

    const [tray, setTray] = useState([]);
    const [assignments, setAssignments] = useState({});
    const [uploadingIds, setUploadingIds] = useState(() => new Set());
    const [savingState, setSavingState] = useState('idle');
    const [failedListingIds, setFailedListingIds] = useState(() => new Set());

    useEffect(() => {
        if (!open) {
            setTray([]);
            setAssignments({});
            setUploadingIds(new Set());
            setSavingState('idle');
            setFailedListingIds(new Set());
            uploadSessionRef.current += 1;
        }
    }, [open]);

    const hasAssignments = Object.values(assignments).some((photos) => photos.length > 0);
    const isDirty = tray.length > 0 || hasAssignments || uploadingIds.size > 0;

    useEffect(() => {
        if (!open || !isDirty) return undefined;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [open, isDirty]);

    const assignToListing = useCallback((listingId, photoId) => {
        setTray((prev) => {
            const photo = prev.find((p) => p.id === photoId);
            if (!photo?.url || !photo?.cloudflareId) return prev;

            setAssignments((a) => ({
                ...a,
                [listingId]: [
                    ...(a[listingId] ?? []),
                    {
                        id: photo.id,
                        url: photo.url,
                        cloudflareId: photo.cloudflareId,
                        label: photo.label,
                    },
                ],
            }));
            setFailedListingIds((prev) => {
                if (!prev.has(listingId)) return prev;
                const next = new Set(prev);
                next.delete(listingId);
                return next;
            });
            return prev.filter((p) => p.id !== photoId);
        });
    }, []);

    const handleUnassign = useCallback((listingId, photoId) => {
        setAssignments((prev) => {
            const photos = prev[listingId] ?? [];
            const photo = photos.find((p) => p.id === photoId);
            if (!photo) return prev;

            setTray((t) => [...t, {
                id: photo.id,
                url: photo.url,
                cloudflareId: photo.cloudflareId,
                label: photo.label ?? '',
            }]);

            const nextPhotos = photos.filter((p) => p.id !== photoId);
            const next = { ...prev };
            if (nextPhotos.length) {
                next[listingId] = nextPhotos;
            } else {
                delete next[listingId];
            }
            return next;
        });
    }, []);

    const handleDragStart = useCallback((e, photo) => {
        if (!photo.url || !photo.cloudflareId) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData('text/photo-id', photo.id);
        e.dataTransfer.setData('text/plain', photo.id);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleFilesUploaded = useCallback(async (fileList) => {
        const imageFiles = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
        if (!imageFiles.length) {
            if (fileList.length) toastError('Only image files are supported.');
            return;
        }

        const session = uploadSessionRef.current;
        const entries = imageFiles.map((file) => ({
            id: createPhotoId(),
            file,
            label: file.name,
        }));

        setTray((prev) => [
            ...prev,
            ...entries.map((e) => ({
                id: e.id,
                label: e.label,
                url: null,
                cloudflareId: null,
            })),
        ]);
        setUploadingIds((prev) => new Set([...prev, ...entries.map((e) => e.id)]));

        await runWithConcurrencyLimit(entries, 3, async (entry) => {
            if (session !== uploadSessionRef.current) return;
            try {
                const { url, cloudflareId } = await uploadPhotoToCloudflare(entry.file);
                if (session !== uploadSessionRef.current) return;
                setTray((prev) => prev.map((p) => (
                    p.id === entry.id ? { ...p, url, cloudflareId } : p
                )));
            } catch {
                toastError(`Upload failed for ${entry.label}`);
                setTray((prev) => prev.filter((p) => p.id !== entry.id));
            } finally {
                setUploadingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(entry.id);
                    return next;
                });
            }
        });
    }, []);

    const requestClose = useCallback(() => {
        if (uploadingIds.size > 0 || isDirty) {
            confirmModal.confirm({
                title: 'Discard pending uploads?',
                message: uploadingIds.size > 0
                    ? 'Photos are still uploading. Closing will discard any unsaved assignments.'
                    : 'Your photo assignments have not been saved yet.',
                confirmLabel: 'Discard',
                cancelLabel: 'Keep editing',
                variant: 'danger',
                onConfirm: onClose,
            });
            return;
        }
        onClose();
    }, [confirmModal, uploadingIds.size, isDirty, onClose]);

    useFocusTrap(modalRef, open, requestClose);

    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) requestClose();
    }, [requestClose]);

    const handleSave = async () => {
        const entries = Object.entries(assignments).filter(([, photos]) => photos.length > 0);
        if (!entries.length) {
            onClose();
            return;
        }

        setSavingState('saving');
        setFailedListingIds(new Set());

        const results = await runWithConcurrencyLimit(entries, 3, async ([listingId, photos]) => {
            try {
                const listing = bulkDrafts.find((l) => l._id === listingId);
                const existing = listing?.photos ?? [];
                const newPhotos = photos.map((p, i) => ({
                    source: 'host',
                    url: p.url,
                    cloudflareId: p.cloudflareId,
                    featured: existing.length === 0 && i === 0,
                }));
                await axios.put(`/listings/${listingId}`, {
                    photos: [...existing, ...newPhotos],
                });
                return { listingId, ok: true };
            } catch (err) {
                return { listingId, ok: false, err };
            }
        });

        const failures = results.filter((r) => !r.ok);
        if (failures.length > 0) {
            setSavingState('error');
            setFailedListingIds(new Set(failures.map((f) => f.listingId)));
            toastError(`${failures.length} of ${results.length} listings failed to save`);
            return;
        }

        toastSuccess(`Saved photos to ${results.length} listing${results.length === 1 ? '' : 's'}`);
        setSavingState('idle');
        onSaved?.();
        onClose();
    };

    if (!open) return null;

    const saveDisabled = savingState === 'saving' || uploadingIds.size > 0;

    return createPortal(
        <>
            <div
                className="bulk-photos-overlay bulk-photos-modal-root open"
                onClick={handleOverlayClick}
            >
                <div
                    ref={modalRef}
                    className="bulk-photos-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="bulkPhotosTitle"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bulk-photos-head">
                        <div>
                            <h3 id="bulkPhotosTitle">Bulk Add Photos</h3>
                            <div className="bulk-photos-head-sub">
                                Drag photos from the tray onto each property · Skip a property to submit without photos
                            </div>
                        </div>
                        <button
                            type="button"
                            className="bulk-photos-close"
                            onClick={requestClose}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>
                    <div className="bulk-photos-body">
                        <div className="bulk-photos-grid">
                            <Tray
                                tray={tray}
                                listings={bulkDrafts}
                                onDropFiles={handleFilesUploaded}
                                onDragStart={handleDragStart}
                                onAssignToListing={assignToListing}
                                uploadingIds={uploadingIds}
                            />
                            <TargetsList
                                listings={bulkDrafts}
                                assignments={assignments}
                                failedListingIds={failedListingIds}
                                onDropOnTarget={assignToListing}
                                onUnassign={handleUnassign}
                            />
                        </div>
                    </div>
                    <div className="bulk-photos-foot">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={requestClose}
                            disabled={savingState === 'saving'}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={saveDisabled}
                            onClick={handleSave}
                        >
                            {savingState === 'saving' ? 'Saving…' : 'Save assignments'}
                        </button>
                    </div>
                </div>
            </div>
            <ConfirmModal {...confirmModal.props} />
        </>,
        document.body,
    );
}
